import { Box3, Vector2Tuple, Vector3 } from 'three';

import { FEnsembleTypesEnum } from '@spazious/dollhouse-shallow-graph';

import {
  FEnsembleRoundSchema,
  FEnsembleRoundSchemaFields,
  FEnsembleRoundValues,
} from '../fensembles/index.js';
import { Schema, SchemaValues } from '../schema/index.js';
import { FInstanceEnsembleNode, FInstanceNode } from '../types/index.js';

import {
  FArrangementGrid,
  FArrangementGridSchemaFields,
  FArrangementGridValues,
} from './FArrangementGrid.js';

export type FArrangementRoundSchemaFields = FArrangementGridSchemaFields &
  FEnsembleRoundSchemaFields;

export interface FArrangementRoundSchema extends Schema {
  fields: FArrangementRoundSchemaFields;
}

export type FArrangementRoundAttributes = Partial<SchemaValues<FArrangementRoundSchemaFields>> &
  FArrangementGridValues &
  FEnsembleRoundValues;

const TAU = Math.PI * 2;
const bbox = new Box3();

export class FArrangementRound extends FArrangementGrid<
  FArrangementRoundSchema,
  FArrangementGridValues,
  FEnsembleRoundSchema,
  FEnsembleRoundValues
> {
  usesEnsembles = true;

  constructor(schema: FArrangementRoundSchema, initialValues?: FArrangementRoundAttributes) {
    super(schema, {
      size: [4, 4],
      chairSize: [0, 0],
      tableRadius: 0,
      maxChairsPerTable: 0,
      ...(initialValues as any),
    });
    this.setValue('getMaxElements', this.getMaxElements);
  }

  getMaxElements = () => {
    const gridElements = this.getMaxGridElements();
    const chairsPerTable = this.getValue('chairsPerTable');
    return gridElements * chairsPerTable;
  };

  getNodeTypes() {
    const { chairType, tableType } = this.getValues();

    if (!chairType) {
      throw new Error('chairType is required');
    }

    if (!tableType) {
      throw new Error('tableType is required');
    }

    return [chairType, tableType];
  }

  updateObjectMeasurements() {
    const [chair, table] = this.getObjects();

    if (!chair) {
      throw new Error('chair table object');
    }

    if (!table) {
      throw new Error('missing table object');
    }

    const { chairDistanceFromTable, chairFanRadius, chairsGap } = this.getValues();

    const tableBoundingBox = bbox.setFromObject(table);
    const tableSize = tableBoundingBox.getSize(new Vector3());
    const tableRadius = Math.max(tableSize.x, tableSize.z) / 2;

    const chairBoundingBox = bbox.setFromObject(chair);
    const chairSize = chairBoundingBox.getSize(new Vector3());
    const chairRadius = chairSize.z / 2;

    // distance of chair center from the table center
    const distanceFromCenter = tableRadius + chairRadius * chairDistanceFromTable;

    const chairWidth = chairSize.x;
    const chairEdgeFromCenter = distanceFromCenter - chairRadius;

    const chairCornerFromCenter = Math.sqrt(
      (chairWidth / 2) * (chairWidth / 2) + chairEdgeFromCenter * chairEdgeFromCenter
    );

    const chairAngle = 2 * Math.atan(chairWidth / (2 * chairEdgeFromCenter));
    const chairGapAngle = 2 * Math.asin(chairsGap / (2 * chairCornerFromCenter));

    const fanAngle = TAU * chairFanRadius;
    const maxChairsPerTable = Math.floor(fanAngle / (chairAngle + chairGapAngle));

    this.updateValues({
      tableRadius,
      chairSize: [chairSize.x, chairSize.z],
      maxChairsPerTable,
    });

    // size of the table+chair composition
    this.setValue('elementSize', this.getElementSize());
  }

  getElementSize(): Vector2Tuple {
    const { chairDistanceFromTable, tableRadius, chairSize } = this.getValues();

    if (!tableRadius || !chairSize[1]) {
      return [0, 0];
    }

    const chairRadius = chairSize[1] / 2;

    // calculate radius to one of back corner of the chair
    const radiusToBackEdgeOfChair =
      tableRadius + chairRadius * chairDistanceFromTable + chairRadius;
    const maxRadius = Math.hypot(radiusToBackEdgeOfChair, chairSize[0] / 2);

    return [maxRadius * 2, maxRadius * 2] as Vector2Tuple;
  }

  getMinimumSize(): Vector2Tuple {
    const { chairDistanceFromTable, tableRadius, chairSize } = this.getValues();

    if (!tableRadius || !chairSize[1]) {
      return [0, 0];
    }

    const chairRadius = chairSize[1] / 2;
    const radiusToBackEdgeOfChair =
      tableRadius + chairRadius * chairDistanceFromTable + chairRadius;
    const maxRadius = Math.hypot(radiusToBackEdgeOfChair, chairSize[0] / 2);

    return [maxRadius * 2, maxRadius * 2] as Vector2Tuple;
  }

  getMaxIterations(maxColumns: number, maxRows: number, ignoreMaxElements: boolean = false) {
    let { elements, chairsPerTable } = this.getValues();
    return this.getSafeIterations(
      Math.min(
        ignoreMaxElements ? Infinity : Math.ceil(elements / chairsPerTable),
        maxColumns * maxRows
      )
    );
  }

  protected getCompositionNodes(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ): FInstanceNode[] {
    let {
      elements,
      tableType,
      chairType,
      chairsPerTable,
      chairFanOffset,
      chairFanRadius,
      tableRadius,
      chairSize,
      chairDistanceFromTable,
    } = this.getValues();

    const nodes: FInstanceNode[] = [];
    const angle = TAU * chairFanRadius;
    const angleOffset = TAU * chairFanOffset;
    const chairRadius = chairSize[1] / 2;

    const distanceFromCenter = tableRadius + chairRadius * chairDistanceFromTable;

    const requiredChairsPerTable = Math.min(elements - idx * chairsPerTable, chairsPerTable);
    for (let i = 0; i < this.getSafeIterations(requiredChairsPerTable); i++) {
      const theta = i * (angle / requiredChairsPerTable) - angle / 2;

      const chairPosition = [
        distanceFromCenter * Math.cos(theta + angleOffset),
        distanceFromCenter * Math.sin(theta + angleOffset),
      ] as Vector2Tuple;

      const chairRotation = Math.PI / 2 - theta - angleOffset;

      nodes.push({
        type: 'chair',
        id: chairType,
        position: this.addVector2Tuples(chairPosition, params.position),
        rotation: chairRotation,
      });
    }

    nodes.push({
      type: 'table',
      id: tableType,
      position: this.addVector2Tuples([0, 0], params.position),
      rotation: 0,
    });

    return nodes;
  }

  addVector2Tuples(a: Vector2Tuple, b: Vector2Tuple): Vector2Tuple {
    return [a[0] + b[0], a[1] + b[1]] as Vector2Tuple;
  }

  getEnsembles(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ): FInstanceEnsembleNode[] {
    let { elements, tableType, chairType, chairsPerTable, chairFanOffset } = this.getValues();

    const requiredChairsPerTable = Math.min(elements - idx * chairsPerTable, chairsPerTable);
    return [
      {
        type: FEnsembleTypesEnum.Round,
        position: this.addVector2Tuples([0, 0], params.position),
        rotation: 0,
        attrs: {
          chairFanOffset,
          tableType,
          chairType,
          chairsPerTable: requiredChairsPerTable,
        },
      },
    ];
  }
}
